import { useEffect, useState } from "react";

import { useParams } from "react-router-dom";
import { Link } from "react-router-dom";

import { ColorSwatchIcon } from "@heroicons/react/outline";

import TinyMCE from "../../components/Shared/TinyMCE";
import LoadingWheel from "../../components/Shared/LoadingWheel";
import classNames from "../../utils/classNames";
import { useThemes } from "../../data/theme/useThemes";
import { useNewsletterThemeSections } from "../../data/newsletterThemeTemplate/useNewsletterThemeSections";
import { useNewsletterThemeSectionMutations } from "../../data/newsletterThemeTemplate/useNewsletterThemeSectionMutations";
import { Section } from "../../models/newsletterThemeTemplate/NewsletterThemeTemplate";


function NewsletterSectionEdit({ section, sectionName, themeId, initialContent, onSave }) {
  const [content, setContent] = useState(initialContent?.emailTemplate?.body || "");
  const [subject, setSubject] = useState(initialContent?.emailTemplate?.subject || "");

  useEffect(() => {
    setContent(initialContent?.emailTemplate?.body || "");
    setSubject(initialContent?.emailTemplate?.subject || "");
  }, [initialContent]);

  const handleSave = () => {
    onSave(section, { subject, body: content });
  };

  return (
    <div className="space-y-4 border-b border-gray-200 pb-6 last:border-b-0">
      <div>
        <h4 className="text-lg font-medium text-gray-900">{sectionName}</h4>
        <p className="mt-1 text-sm text-gray-500">
          Edit the {sectionName.toLowerCase()} section content.
        </p>
      </div>


      <div>
        <TinyMCE
          value={content}
          onChange={setContent}
          height={600}
          showCodeView={true}
        />
      </div>

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSave}
          className="px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md shadow-sm text-white bg-etpink-600 hover:bg-etpink-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-etpink-400"
        >
          Save {sectionName}
        </button>
      </div>
    </div>
  );
}

function NewsletterTemplateEdit({ themeId, themeName }) {
  const { sections } = useNewsletterThemeSections(themeId);
  const { updateSection } = useNewsletterThemeSectionMutations(themeId);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    document.title = `EstateTrack Portal | Edit Newsletter Templates - ${themeName}`;
  }, [themeId, themeName]);

  const sectionNames = {
    [Section.Main]: "Main",
    [Section.Article]: "Article",
    [Section.Property]: "Property",
    [Section.Testimonial]: "Testimonial",
    [Section.BranchDetails]: "Branch Details",
  };

  const sectionOrder = [
    Section.Main,
    Section.Article,
    Section.Property,
    Section.BranchDetails,
    Section.Testimonial,
  ];

  const handleSave = (section, data) => {
    setIsSaving(true);
    updateSection.mutate(
      { section, data },
      {
        onSettled: () => {
          setIsSaving(false);
        },
      }
    );
  };

  const getSectionContent = (section) => {
    if (!sections.data?.data) return null;
    return sections.data.data.find((s) => s.section === section);
  };

  if (sections.isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingWheel width="w-10" height="h-10" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-xl font-medium text-gray-900">Edit Newsletter Templates</h3>
        <p className="mt-1 text-sm text-gray-500">
          Edit newsletter template sections for the {themeName} theme.
        </p>
      </div>

      {sectionOrder.map((section) => {
        const sectionContent = getSectionContent(section);
        const sectionName = sectionNames[section];

        return (
          <NewsletterSectionEdit
            key={section}
            section={section}
            sectionName={sectionName}
            themeId={themeId}
            initialContent={sectionContent}
            onSave={handleSave}
          />
        );
      })}
    </div>
  );
}

export default function NewsletterTemplateManager() {
  const { themes } = useThemes(true); // Include inactive themes
  const { themeId } = useParams();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (!themes.isLoading && themes.data) {
      setIsLoading(false);
    }
  }, [themes.isLoading, themes.data]);

  const getPanel = () => {
    const themesList = themes.data?.data || [];
    
    if (!themeId && themesList.length > 0) {
      // Default to first theme if no theme selected
      const firstTheme = themesList[0];
      return <NewsletterTemplateEdit themeId={firstTheme.id} themeName={firstTheme.name} />;
    }

    const selectedTheme = themesList.find((theme) => theme.id === themeId);
    if (selectedTheme) {
      return <NewsletterTemplateEdit themeId={selectedTheme.id} themeName={selectedTheme.name} />;
    }

    return (
      <div className="space-y-4">
        <div>
          <h3 className="text-xl font-medium text-gray-900">Select a Theme</h3>
          <p className="mt-1 text-sm text-gray-500">
            Please select a theme from the sidebar to edit its newsletter templates.
          </p>
        </div>
      </div>
    );
  };

  const subNavigation = () => {
    const themesList = themes.data?.data || [];
    
    return themesList.map((theme) => ({
      icon: ColorSwatchIcon,
      name: theme.name,
      href: theme.id,
      id: theme.id,
    }));
  };

  if (isLoading || themes.isLoading) {
    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="h-80 max-w-7xl flex items-center justify-center">
          <LoadingWheel width="w-10" height="h-10" />
        </div>
      </div>
    );
  } else {
    return (
      <>
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="min-h-[256px] divide-y divide-gray-200 lg:grid lg:grid-cols-12 lg:divide-y-0 lg:divide-x">
              <aside className="py-6 lg:col-span-2">
                <nav className="space-y-1">
                  {subNavigation().map((item) => {
                    const themesList = themes.data?.data || [];
                    let currentThemeId = themeId || (themesList.length > 0 ? themesList[0].id : "");
                    let current = item.id === currentThemeId;
                    return (
                      <Link
                        key={item.id}
                        to={`${item.href}`}
                        className={classNames(
                          current
                            ? "bg-etpink-50 border-etpink-500 text-etpink-700 hover:bg-etpink-50 hover:text-etpink-700"
                            : "border-transparent text-gray-900 hover:bg-gray-50 hover:text-gray-900",
                          "group border-l-4 px-3 py-2 flex items-center text-sm font-medium"
                        )}
                        aria-current={current ? "page" : undefined}
                      >
                        <item.icon
                          className={classNames(
                            current
                              ? "text-etpink-500 group-hover:text-etpink-500"
                              : "text-gray-400 group-hover:text-gray-500",
                            "flex-shrink-0 -ml-1 mr-3 h-6 w-6"
                          )}
                          aria-hidden="true"
                        />
                        <span className="truncate">{item.name}</span>
                      </Link>
                    );
                  })}
                </nav>
              </aside>
              <div className="bg-gray-50 lg:col-span-10">
                <div className="h-full py-6 px-4 sm:p-6 lg:pb-8">
                  {getPanel()}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

